import { Component, ElementRef, OnDestroy, signal, viewChild, inject, computed, Signal, afterNextRender, effect } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { ResolutionService } from '../resolution.service';
import { SignalsStoreService } from '../signals-store.service';

@Component({
  selector: 'app-shop-slider-banner',
  imports: [],
  templateUrl: './shop-slider-banner.component.html',
  styleUrl: './shop-slider-banner.component.scss'
})
export class ShopSliderBannerComponent implements OnDestroy {

  //#region Services

  #resolutionService = inject(ResolutionService);
  #signalsStoreService = inject(SignalsStoreService);
  #document = inject(DOCUMENT);

  //#endregion

  //#region Properties

  shopSlider = viewChild.required<ElementRef>('shopSlider');

  slides = signal<{
    title: string;
    boldSubtitle: string;
    subtitle: string;
    mobileImgSm: string;
    mobileImg: string;
    desktopImg: string;
    objectPosition?: 'center' | 'bottom';
  }[]>([
    {
      title: 'REGENERATIVE',
      boldSubtitle: '& SUSTAINABLE',
      subtitle: '',
      mobileImgSm: '/assets/images/landing/home/carousel/1-mobile-sm.webp',
      mobileImg: '/assets/images/landing/home/carousel/1-mobile.webp',
      desktopImg: '/assets/images/landing/home/carousel/1-desktop.webp',
    },
    {
      title: 'FARM FRESH',
      boldSubtitle: '',
      subtitle: 'Fruits & Vegetables',
      mobileImgSm: '/assets/images/landing/home/carousel/2-mobile-sm.webp',
      mobileImg: '/assets/images/landing/home/carousel/2-mobile.webp',
      desktopImg: '/assets/images/landing/home/carousel/2-desktop.webp',
    },
    {
      title: 'EAT HEALTY',
      boldSubtitle: '',
      subtitle: 'Be Healthy',
      mobileImgSm: '/assets/images/landing/home/carousel/3-mobile-sm.webp',
      mobileImg: '/assets/images/landing/home/carousel/3-mobile.webp',
      desktopImg: '/assets/images/landing/home/carousel/3-desktop.webp',
    },
    {
      title: 'REGENERATIVE',
      boldSubtitle: '& SUSTAINABLE',
      subtitle: '',
      // bg-3 historically used a single image with `background-position: bottom`
      mobileImgSm: '/assets/images/landing/home/carousel/4.webp',
      mobileImg: '/assets/images/landing/home/carousel/4.webp',
      desktopImg: '/assets/images/landing/home/carousel/4.webp',
      objectPosition: 'bottom',
    },
  ]);

  isMobile = computed(() => this.#resolutionService.isMobile());

  isSmallMobile = computed(() => this.#resolutionService.isSmallMobile());

  slider: KeenSliderInstance | any = null;

  dotHelper = signal<number[]>([]);

  currentSlide = 0;

  isShopBannerHidden = computed(() => this.#signalsStoreService.isShopBannerHidden());

  hasSession = computed(() => this.#signalsStoreService.hasSession());

  isSessionLoaded = computed(() => this.#signalsStoreService.isSessionLoaded());

  #destroyed = false;
  #initRetries = 0;

  // Slides 2+ are gated behind this signal so their <img> requests don't
  // compete with the LCP image (slide 0). Flips true after first paint via
  // requestIdleCallback / 2s fallback — well before the 5s auto-advance.
  revealSecondarySlides = signal(false);

  //#endregion

  //#region Constructors

  constructor() {
    this.#preloadLcpBanner();

    afterNextRender(() => {
      this.#setUpDefaultSlider();
      this.#scheduleSecondarySlideReveal();
    });

    effect(() => {
      const hidden = this.isShopBannerHidden();
      if (!hidden && this.slider) {
        setTimeout(() => this.slider?.update(), 250);
      }
    });
  }

  #scheduleSecondarySlideReveal() {
    const reveal = () => {
      if (this.#destroyed) return;
      if (!this.revealSecondarySlides()) this.revealSecondarySlides.set(true);
    };

    const win = globalThis as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void };
    if (typeof win.requestIdleCallback === 'function') {
      win.requestIdleCallback(reveal, { timeout: 2500 });
    } else {
      setTimeout(reveal, 2000);
    }
  }

  ngOnDestroy() {
    this.#destroyed = true;
    if (this.slider) this.slider.destroy();
  }

  //#endregion

  //#region Methods

  // Background-image LCP can't carry fetchpriority="high", so we surface the
  // first-slide URL in <head> as <link rel="preload"> — runs in SSR so it ships
  // in the initial HTML.
  #preloadLcpBanner() {
    if (this.#document.head.querySelector('link[data-shop-banner-preload]')) return;

    const variants: Array<{ href: string; media: string }> = [
      { href: '/assets/images/landing/home/carousel/1-desktop.webp', media: '(min-width: 768px)' },
      { href: '/assets/images/landing/home/carousel/1-mobile-sm.webp', media: '(max-width: 767.98px)' },
    ];

    for (const { href, media } of variants) {
      const link = this.#document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      link.media = media;
      link.setAttribute('fetchpriority', 'high');
      link.setAttribute('data-shop-banner-preload', '');
      this.#document.head.appendChild(link);
    }
  }

  #setUpDefaultSlider() {

    if (this.#destroyed) return;
    if (!(this.shopSlider()?.nativeElement)) return;

    const width = this.shopSlider().nativeElement.getBoundingClientRect().width ?? 0;
    if (width === 0) {
      if (this.#initRetries++ < 3) {
        requestAnimationFrame(() => this.#setUpDefaultSlider());
      }
      return;
    }

    this.slider = new KeenSlider(this.shopSlider().nativeElement, {
      initial: this.currentSlide,
      renderMode: 'performance',
      loop: this.slides().length > 1,
      drag: false,
      slideChanged: s => {
        this.currentSlide = s.track.details.rel;
        // Safety net: if the slider advances (auto or via dot click) before the
        // idle-callback fires, reveal secondary slides immediately so the next
        // image has time to load.
        if (!this.revealSecondarySlides()) this.revealSecondarySlides.set(true);
      }
    }, [
      (slider) => {
        let timeout: NodeJS.Timeout | null = null;
        let mouseOver = false
        function clearNextTimeout() {
          if (timeout)
            clearTimeout(timeout);
        }
        function nextTimeout() {

          if (timeout)
            clearTimeout(timeout);

          if (mouseOver) return
          timeout = setTimeout(() => {
            slider.next()
          }, 1000 * 5)
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true
            clearNextTimeout()
          })
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false
            nextTimeout()
          })
          nextTimeout()
        })
        slider.on("dragStarted", clearNextTimeout)
        slider.on("animationEnded", nextTimeout)
        slider.on("updated", nextTimeout)
      }
    ]);

    if (this.slider.track?.details?.slides?.length) {
      this.dotHelper.set([
        ...Array(this.slider.track.details.slides.length)
      ]);
    }
  }

  // #getSlides() {

  //   const version = 4; // Importat to change this version number when updating the images
  //   this.slides.set([
  //     {
  //       title: '<p class="m-0 text-white">Fresh & Healthy.</p>',
  //       text: `<p class="m-0">Wholesome Organic Food</p>`,
  //       img: computed(() => {
  //         // const tablet = this.isMobile();
  //         // const mobile = this.isSmallMobile();
  //         // const folder = mobile ? 'Mobile' : tablet ? 'Tablet' : 'Desktop';
  //         // return `/assets/images/shop-slider/${folder}/1.png?v=${version}`
  //         return `/assets/images/landing/home/carousel/1.webp?v=${version}`
  //       })
  //     },
  //     {
  //       title: '<p class="m-0 text-white">Fresh & Healthy.</p>',
  //       text: `<p class="m-0">Wholesome Organic Food</p>`,
  //       img: computed(() => {
  //         // const tablet = this.isMobile();
  //         // const mobile = this.isSmallMobile();
  //         // const folder = mobile ? 'Mobile' : tablet ? 'Tablet' : 'Desktop';
  //         // return `/assets/images/shop-slider/${folder}/1.png?v=${version}`
  //         return `/assets/images/landing/home/carousel/2.webp?v=${version}`
  //       })
  //     },
  //     {
  //       title: '<p class="m-0 text-white">Fresh & Healthy.</p>',
  //       text: `<p class="m-0">Wholesome Organic Food</p>`,
  //       img: computed(() => {
  //         // const tablet = this.isMobile();
  //         // const mobile = this.isSmallMobile();
  //         // const folder = mobile ? 'Mobile' : tablet ? 'Tablet' : 'Desktop';
  //         // return `/assets/images/shop-slider/${folder}/1.png?v=${version}`
  //         return `/assets/images/landing/home/carousel/3.webp?v=${version}`
  //       })
  //     },
  //     {
  //       title: '<p class="m-0 text-white">Fresh & Healthy.</p>',
  //       text: `<p class="m-0">Wholesome Organic Food</p>`,
  //       img: computed(() => {
  //         // const tablet = this.isMobile();
  //         // const mobile = this.isSmallMobile();
  //         // const folder = mobile ? 'Mobile' : tablet ? 'Tablet' : 'Desktop';
  //         // return `/assets/images/shop-slider/${folder}/1.png?v=${version}`
  //         return `/assets/images/landing/home/carousel/4.webp?v=${version}`
  //       })
  //     }
  //   ]);

  //   setTimeout(() => {
  //     this.#setUpDefaultSlider();
  //   });
  // }

  //#endregion
}
